import { GreenLightVerdictResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/green-light/green-light.response';
import { GreenLightSoilDayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/green-light/green-light.response';
import { GreenLightStatus } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/green-light.enum';
import { formatVwNumber } from '../@commons/pipes/vw-date.pipe';

/**
 * Feu vert (NF-2) : regroupements, filtres et dates en clair, sans appel réseau.
 * Les dates viennent du back au format YYYY-MM-DD.
 */

export type GreenLightFilter = 'garden' | 'seeds' | 'all';

/** Ordre des groupes de la liste (maquette M11) : ce qui se fait maintenant d'abord. */
export const STATUS_ORDER: GreenLightStatus[] = [
  GreenLightStatus.GO,
  GreenLightStatus.LAST_CHANCE,
  GreenLightStatus.SOON,
  GreenLightStatus.NOT_YET,
  GreenLightStatus.INDOOR,
];


/**
 * Couleur de la pastille, toujours avec son mot (K13) : « Feu vert » sur fond vert pâle,
 * « Bientôt » et « Dernière chance » en ambre, « Pas encore » en gris, « Au chaud » comme
 * les semis au chaud du calendrier.
 */
export const STATUS_CHIP_CLASSES: Record<GreenLightStatus, string> = {
  [GreenLightStatus.GO]: 'bg-vw-surface-tint text-vw-primary-ink',
  [GreenLightStatus.LAST_CHANCE]: 'bg-vw-warn-bg text-vw-warn-ink',
  [GreenLightStatus.SOON]: 'bg-vw-warn-bg text-vw-warn-ink',
  [GreenLightStatus.NOT_YET]: 'bg-vw-surface-sunk text-vw-body',
  [GreenLightStatus.INDOOR]: 'bg-vw-warn-bg text-vw-warn-ink',
};

/** Une culture « à moi » : au potager, dans le plan de saison ou en sachet. */
export function isMine(verdict: GreenLightVerdictResponse): boolean {
  return verdict.inGarden || verdict.inPlan || verdict.inSeeds;
}

/** « Mon potager » : ce qui pousse ou est prévu ; « Mes graines » : les sachets ; « Tout ». */
export function filterVerdicts(items: GreenLightVerdictResponse[], filter: GreenLightFilter): GreenLightVerdictResponse[] {
  switch (filter) {
    case 'garden':
      return items.filter((v) => v.inGarden || v.inPlan);
    case 'seeds':
      return items.filter((v) => v.inSeeds);
    default:
      return items;
  }
}

/** Groupes par verdict, dans l'ordre de la maquette ; groupes vides omis. */
export function groupVerdicts(
  items: GreenLightVerdictResponse[],
): { status: GreenLightStatus; items: GreenLightVerdictResponse[] }[] {
  return STATUS_ORDER.map((status) => ({ status, items: items.filter((v) => v.status === status) })).filter(
    (group) => group.items.length > 0,
  );
}

/**
 * Les lignes Feu vert de l'accueil n'apparaissent qu'à partir du 15 mars (K22 : aucun
 * changement d'accueil en janvier, rien en février). Le serveur décide ensuite de la saison :
 * hors saison, sa liste est vide.
 */
export function isGreenLightHomePeriod(today: Date = new Date()): boolean {
  const month = today.getMonth() + 1;
  return month > 3 || (month === 3 && today.getDate() >= 15);
}

const utcDate = (isoDate: string): Date => {
  const [year, month, day] = isoDate.substring(0, 10).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

/** « 20 mai » */
export function dayMonth(isoDate: string, lang: string): string {
  return new Intl.DateTimeFormat((lang || 'fr').toLowerCase(), { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    utcDate(isoDate),
  );
}

/** « mardi 11 mai » */
export function weekdayDayMonth(isoDate: string, lang: string): string {
  return new Intl.DateTimeFormat((lang || 'fr').toLowerCase(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(utcDate(isoDate));
}

/** Jours entiers entre deux dates YYYY-MM-DD (négatif si `to` est passé). */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcDate(to).getTime() - utcDate(from).getTime()) / 86_400_000);
}

export interface SoilCurve {
  /** Points de la courbe, « x,y » séparés par des espaces (viewBox 326 x 120). */
  points: string;
  /** Ordonnée du seuil de la culture. */
  thresholdY: number;
  /** Premier jour au-dessus du seuil, s'il existe. */
  crossing?: { x: number; y: number; date: string };
  first: { x: number; y: number; soilC: number };
}

const CURVE_WIDTH = 326;
const CURVE_TOP = 16;
const CURVE_BOTTOM = 96;

/**
 * Courbe du sol sur les jours de prévision (Premium), avec la ligne du seuil : l'échelle
 * englobe le seuil pour qu'on voie toujours la distance qui reste.
 */
export function soilCurve(series: GreenLightSoilDayResponse[] | null | undefined, neededC?: number | null): SoilCurve | null {
  if (!series || series.length < 2) return null;
  const values = series.map((d) => d.soilC);
  if (neededC != null) values.push(neededC);
  const min = Math.floor(Math.min(...values) - 1);
  const max = Math.ceil(Math.max(...values) + 1);
  const y = (c: number) => Math.round(CURVE_BOTTOM - ((c - min) / (max - min)) * (CURVE_BOTTOM - CURVE_TOP));
  const x = (i: number) => Math.round((i / (series.length - 1)) * CURVE_WIDTH);
  const points = series.map((d, i) => `${x(i)},${y(d.soilC)}`).join(' ');
  const crossingIndex = neededC != null ? series.findIndex((d) => d.soilC >= neededC) : -1;
  return {
    points,
    thresholdY: neededC != null ? y(neededC) : CURVE_TOP,
    crossing:
      crossingIndex > 0
        ? { x: x(crossingIndex), y: y(series[crossingIndex].soilC), date: series[crossingIndex].date }
        : undefined,
    first: { x: x(0), y: y(series[0].soilC), soilC: series[0].soilC },
  };
}

/**
 * Clé au singulier quand le nombre de jours vaut 1 (« attends 1 jour ») : les six
 * langues de l'app ne mettent le singulier qu'à 1 pour une durée.
 */
export function daysKey(key: string, days: number | null | undefined): string {
  return days === 1 ? `${key}_one` : key;
}

/** Température dans la langue de l'app : « 10,5 » en français, « 10.5 » en anglais. */
export function degrees(value: number | null | undefined, lang: string): string {
  return formatVwNumber(value, lang);
}

export interface TextKey {
  key: string;
  params: Record<string, unknown>;
}

